import {SheetModal} from 'app/pages/school/Schedule/components/timetable/pages/workLoadBreakDown/modals/components/SheetModal'
import {Check, Copy, GraduationCap, Plus, Trash2} from 'lucide-react'
import React, {FC, useId, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'

import {
  LevelClassDraft,
  LevelDraft,
  levelIssues,
  newDraftId,
  parseSectionNames,
  resizeSections,
  uniqueStrings,
} from '../utils/academicStructure'
import WizardConfirmDialog from './WizardConfirmDialog'

const MAX_SECTIONS = 26

interface Props {
  mode: 'edit' | 'add'
  curriculum: string
  /** The level being edited; null when adding. */
  level: LevelDraft | null
  /** Names of the other levels in this curriculum (for the uniqueness check). */
  siblingNames: readonly string[]
  /** Stored class names of this curriculum's other levels (class names are unique per curriculum). */
  siblingClassNames?: readonly string[]
  /**
   * Fixed prefix written into the name of every class added here ("FBISE" → "FBISE Class 1"),
   * matching how this curriculum's prefilled classes are already named. Defaults to the
   * curriculum name. Empty when the school uses plain names (the curriculum is stored on the
   * level instead): nothing is added.
   */
  classNamePrefix?: string
  /** Every subject this curriculum can offer — shown as chips, selected ones highlighted. */
  subjectPool: readonly string[]
  /** Sections a new class starts with. */
  defaultSections: readonly string[]
  isDark: boolean
  /** Icon shown next to the level name in the edit banner. */
  levelIcon?: React.ReactNode
  onSave: (level: LevelDraft) => void
  /** Edit mode only — the sticky footer's Delete level. */
  onDelete?: () => void
  /**
   * Edit mode only — "Apply these sections to all levels". Called AFTER the user confirms;
   * the host copies the section settings onto every other level immediately.
   */
  onApplySectionsToAllLevels?: (sections: string[]) => void
  onClose: () => void
  /**
   * The warning added to a delete's confirm when the classes going have students ("1 student is
   * in … they'll show as 'Class not found'"); null when nobody is in them.
   */
  studentsWarningFor?: (classes: LevelDraft['classes']) => string | null
}

const makeTokens = (isDark: boolean) => ({
  isDark,
  text: isDark ? '#ffffff' : '#181C32',
  muted: isDark ? '#92929f' : '#7E8299',
  border: isDark ? '#3f4254' : '#e4e6ef',
  inputBg: isDark ? '#1e1e2e' : '#ffffff',
  accent: '#056EE9',
  danger: '#f1416c',
})
type Tokens = ReturnType<typeof makeTokens>

const inputStyle = (t: Tokens): React.CSSProperties => ({
  background: t.inputBg,
  color: t.text,
  border: `1px solid ${t.border}`,
  borderRadius: 10,
  padding: '0.55rem 0.75rem',
  outline: 'none',
  width: '100%',
  fontSize: '0.92rem',
  minWidth: 0,
})

const newClass = (defaultSections: readonly string[]): LevelClassDraft => ({
  id: newDraftId('cls'),
  name: '',
  sections: [...defaultSections],
})

const cloneLevel = (level: LevelDraft): LevelDraft => ({
  ...level,
  classes: level.classes.map((cls) => ({...cls, sections: [...cls.sections]})),
  subjects: [...level.subjects],
})

/** The students line under a delete confirm's body (see `studentsWarningFor`). */
export const StudentsWarning: FC<{text: string}> = ({text}) => (
  <p className='step3-delete-students-warning'>{text}</p>
)

/**
 * Right-side drawer for one academic level (edit or add): level name, classes with their
 * sections, subjects — worked on as a local draft. Save commits the draft to the host in one
 * call; Cancel/close asks before discarding unsaved edits.
 */
const Step3LevelDrawer: FC<Props> = ({
  mode,
  curriculum,
  level,
  siblingNames,
  siblingClassNames = [],
  classNamePrefix,
  subjectPool,
  defaultSections,
  isDark,
  levelIcon,
  onSave,
  onDelete,
  onApplySectionsToAllLevels,
  onClose,
  studentsWarningFor,
}) => {
  const intl = useIntl()
  const t = (suffix: string, values?: Record<string, any>) =>
    intl.formatMessage({id: `ONBOARDING_WIZARD.STEP3_REVIEW.${suffix}` as any}, values)
  const tokens = makeTokens(isDark)
  const nameInputId = useId()
  const customSubjectId = useId()

  // Classes added in the wizard carry the curriculum prefix at the START of their name -
  // "FBISE Class 1", the same shape as this curriculum's prefilled classes - so the same class
  // name under two boards never collides on the server, where students and subjects resolve a
  // class by bare name. The prefix is a fixed, non-editable part of the field: the draft holds
  // the typed base name, `taggedClassIds` says which classes get the prefix back on save.
  // Classes that came in without it (older data) stay as they are.
  const curriculumTag = (classNamePrefix ?? curriculum).trim()
  const hasCurriculumTag = (name: string) =>
    name.trim().toLowerCase().startsWith(`${curriculumTag.toLowerCase()} `)
  const stripCurriculumTag = (name: string) =>
    hasCurriculumTag(name) ? name.trim().slice(curriculumTag.length).trimStart() : name
  // An empty tag (plain names, see levelCurriculum.ts) adds nothing.
  const withCurriculumTag = (base: string) =>
    curriculumTag ? `${curriculumTag} ${base.trim()}` : base.trim()

  const [initial] = useState<{draft: LevelDraft; tagged: Set<string>; levelTagged: boolean}>(() => {
    const base: LevelDraft = level
      ? cloneLevel(level)
      : {id: newDraftId('lvl'), name: '', classes: [newClass(defaultSections)], subjects: []}
    // Existing level: tagged = whichever classes already carry the prefix. New level: all
    // of them. The level name follows the same rule.
    const tagged = new Set(
      base.classes.filter((cls) => !level || hasCurriculumTag(cls.name)).map((cls) => cls.id)
    )
    const levelTagged = !level || hasCurriculumTag(base.name)
    return {
      draft: {
        ...base,
        name: levelTagged ? stripCurriculumTag(base.name) : base.name,
        classes: base.classes.map((cls) =>
          tagged.has(cls.id) ? {...cls, name: stripCurriculumTag(cls.name)} : cls
        ),
      },
      tagged,
      levelTagged,
    }
  })
  const initialDraft = initial.draft
  const levelTagged = initial.levelTagged
  const [taggedClassIds, setTaggedClassIds] = useState<Set<string>>(initial.tagged)
  const [draft, setDraft] = useState<LevelDraft>(initialDraft)
  // Free text of each class's "Section names" field; parsed into sections on blur/Enter.
  const [sectionText, setSectionText] = useState<Record<string, string>>(() =>
    Object.fromEntries(initialDraft.classes.map((cls) => [cls.id, cls.sections.join(', ')]))
  )
  // Subjects typed in this session that are not in the curriculum pool — stay listed even
  // after being unticked so the user can re-select them.
  const [customSubjects, setCustomSubjects] = useState<string[]>([])
  const [customSubjectInput, setCustomSubjectInput] = useState('')
  // Typed subject that is already in the list: shown right away, cleared on the next keystroke.
  const [subjectAlreadyAdded, setSubjectAlreadyAdded] = useState(false)
  const [confirm, setConfirm] = useState<
    'discard' | 'delete' | 'applyAll' | {removeClass: string} | null
  >(null)
  // Validation messages only appear once the user tries to save an incomplete level — an
  // empty Add drawer must not open covered in red.
  const [showErrors, setShowErrors] = useState(false)

  const isDirty = JSON.stringify(draft) !== JSON.stringify(initialDraft)

  const setClass = (classId: string, patch: Partial<LevelClassDraft>) =>
    setDraft((prev) => ({
      ...prev,
      classes: prev.classes.map((cls) => (cls.id === classId ? {...cls, ...patch} : cls)),
    }))

  const setClassSections = (classId: string, sections: string[]) => {
    setClass(classId, {sections})
    setSectionText((prev) => ({...prev, [classId]: sections.join(', ')}))
  }

  const commitSectionText = (cls: LevelClassDraft) => {
    const parsed = parseSectionNames(sectionText[cls.id] ?? '')
    if (parsed.length === 0) {
      setSectionText((prev) => ({...prev, [cls.id]: cls.sections.join(', ')}))
      return
    }
    setClassSections(cls.id, parsed.slice(0, MAX_SECTIONS))
  }

  const handleCountChange = (cls: LevelClassDraft, raw: string) => {
    const parsed = Number.parseInt(raw, 10)
    if (!Number.isFinite(parsed)) return
    const count = Math.min(MAX_SECTIONS, Math.max(1, parsed))
    setClassSections(cls.id, resizeSections(cls.sections, count))
  }

  const addClass = () => {
    const cls = newClass(defaultSections)
    setDraft((prev) => ({...prev, classes: [...prev.classes, cls]}))
    setSectionText((prev) => ({...prev, [cls.id]: cls.sections.join(', ')}))
    setTaggedClassIds((prev) => new Set(prev).add(cls.id))
  }

  // Counted on the level as saved (its stored class names): a class added in this drawer has
  // nobody in it yet.
  const levelStudentsWarning =
    confirm === 'delete' && level ? (studentsWarningFor?.(level.classes) ?? null) : null
  const classStudentsWarning = (() => {
    if (typeof confirm !== 'object' || confirm === null || !level) return null
    const saved = level.classes.find((cls) => cls.id === confirm.removeClass)
    return saved ? (studentsWarningFor?.([saved]) ?? null) : null
  })()

  const removeClass = (classId: string) =>
    setDraft((prev) => ({...prev, classes: prev.classes.filter((cls) => cls.id !== classId)}))

  /** The section setup the "apply" actions copy: the first class that has any sections. */
  const templateSections = draft.classes.find((cls) => cls.sections.length > 0)?.sections ?? []

  const applySectionsToAllClasses = () => {
    if (templateSections.length === 0) return
    setDraft((prev) => ({
      ...prev,
      classes: prev.classes.map((cls) => ({...cls, sections: [...templateSections]})),
    }))
    setSectionText((prev) => {
      const next = {...prev}
      draft.classes.forEach((cls) => {
        next[cls.id] = templateSections.join(', ')
      })
      return next
    })
  }

  const confirmApplyToAllLevels = () => {
    setConfirm(null)
    if (templateSections.length === 0) return
    applySectionsToAllClasses()
    onApplySectionsToAllLevels?.([...templateSections])
  }

  const chipSubjects = useMemo(
    () => uniqueStrings([...subjectPool, ...customSubjects, ...draft.subjects]),
    [subjectPool, customSubjects, draft.subjects]
  )
  const isSelected = (name: string) =>
    draft.subjects.some((subject) => subject.toLowerCase() === name.toLowerCase())

  const toggleSubject = (name: string) =>
    setDraft((prev) => ({
      ...prev,
      subjects: prev.subjects.some((subject) => subject.toLowerCase() === name.toLowerCase())
        ? prev.subjects.filter((subject) => subject.toLowerCase() !== name.toLowerCase())
        : [...prev.subjects, name],
    }))

  const addCustomSubject = () => {
    const name = customSubjectInput.trim()
    if (!name) return
    const existing = chipSubjects.find((subject) => subject.toLowerCase() === name.toLowerCase())
    if (existing) {
      setSubjectAlreadyAdded(true)
      return
    }
    setCustomSubjects((prev) => [...prev, name])
    toggleSubject(name)
    setCustomSubjectInput('')
  }

  // --- Validation -------------------------------------------------------------------
  const issues = levelIssues(draft)
  // The name as it will be saved: prefix + typed part for a tagged level.
  const fullLevelName = levelTagged ? withCurriculumTag(draft.name) : draft.name.trim()
  const duplicateLevelName =
    draft.name.trim().length > 0 &&
    siblingNames.some((name) => name.trim().toLowerCase() === fullLevelName.toLowerCase())
  const classNames = draft.classes.map((cls) => cls.name.trim().toLowerCase()).filter(Boolean)
  const duplicateClassName = new Set(classNames).size !== classNames.length
  // The class name as it will be saved, checked against this curriculum's other levels.
  const siblingClassNameSet = useMemo(
    () => new Set(siblingClassNames.map((name) => name.trim().toLowerCase())),
    [siblingClassNames]
  )
  const classExistsInCurriculum = (cls: LevelClassDraft) => {
    if (cls.name.trim().length === 0) return false
    const fullName = taggedClassIds.has(cls.id) ? withCurriculumTag(cls.name) : cls.name.trim()
    return siblingClassNameSet.has(fullName.toLowerCase())
  }
  const classNameInOtherLevel = draft.classes.some(classExistsInCurriculum)
  // A class row the user added but never named must not be dropped silently on save — the
  // level would look saved while the new class vanished.
  const isBlankClassName = (cls: LevelClassDraft) => cls.name.trim().length === 0
  const blankClassName = draft.classes.some(isBlankClassName)
  const canSave =
    issues.length === 0 &&
    !duplicateLevelName &&
    !duplicateClassName &&
    !classNameInOtherLevel &&
    !blankClassName
  const showError = (visible: boolean) => showErrors && visible

  const handleSave = () => {
    if (!canSave) {
      setShowErrors(true)
      return
    }
    onSave({
      ...draft,
      name: fullLevelName,
      classes: draft.classes
        .map((cls) => ({...cls, name: cls.name.trim(), sections: uniqueStrings(cls.sections)}))
        .filter((cls) => cls.name.length > 0)
        // The curriculum tag goes back on here, so it is in manualData and every payload.
        .map((cls) =>
          taggedClassIds.has(cls.id) ? {...cls, name: withCurriculumTag(cls.name)} : cls
        ),
      subjects: uniqueStrings(draft.subjects),
    })
  }

  const requestClose = () => {
    if (isDirty) setConfirm('discard')
    else onClose()
  }

  // --- Rendering helpers ------------------------------------------------------------
  const sectionHeader = (step: number, title: string, hint: string) => (
    <div className='d-flex align-items-start gap-3 mb-3'>
      <span className='step3-sheet-step-badge'>{step}</span>
      <div>
        <div style={{color: tokens.text, fontWeight: 700, fontSize: '0.98rem'}}>{title}</div>
        <div style={{color: tokens.muted, fontSize: '0.82rem'}}>{hint}</div>
      </div>
    </div>
  )

  const errorText = (message: string) => (
    <div style={{color: tokens.danger, fontSize: '0.8rem', fontWeight: 600, marginTop: '0.4rem'}}>
      {message}
    </div>
  )

  return (
    <>
      <SheetModal
        show
        onHide={requestClose}
        title={mode === 'edit' ? t('DRAWER_EDIT_TITLE') : t('DRAWER_ADD_TITLE')}
        icon=''
        showCloseButton
        // Wider than the Institution Setup sheets: every class row carries the curriculum
        // prefix ("Cambridge (CAIE) Class 1", or a long custom name) in front of the editable
        // part, and at 480px a long prefix left no visible room for the class name.
        maxWidth='640px'
        // Same sheet as the Institution Setup editors: wizard gradient surface, and the
        // review page squeezed aside (push) instead of dimmed, see _onboardingwizard.scss.
        sheetClassName='step2-edit-sheet step3-level-sheet'
        push
      >
        <div className='step3-level-sheet-body'>
          {mode === 'add' && (
            <p style={{color: tokens.muted, fontSize: '0.9rem', lineHeight: 1.6, margin: 0}}>
              {t('DRAWER_ADD_SUBTITLE')}
            </p>
          )}

          <div className='step3-sheet-banner'>
            <span className='step3-sheet-banner-icon'>
              {mode === 'edit' ? (
                (levelIcon ?? <GraduationCap size={18} />)
              ) : (
                <GraduationCap size={18} />
              )}
            </span>
            <div style={{minWidth: 0}}>
              {mode === 'edit' ? (
                <>
                  <div style={{color: tokens.text, fontWeight: 700, fontSize: '0.95rem'}}>
                    {level ? stripCurriculumTag(level.name) : initialDraft.name}
                  </div>
                  <div style={{color: tokens.muted, fontSize: '0.82rem'}}>{curriculum}</div>
                </>
              ) : (
                <div style={{color: tokens.text, fontWeight: 700, fontSize: '0.95rem'}}>
                  {curriculum}
                </div>
              )}
            </div>
          </div>

          {/* 1. Level name */}
          <section>
            {sectionHeader(1, t('LEVEL_NAME'), t('LEVEL_NAME_HINT'))}
            {levelTagged ? (
              // Same shape as the class name fields: only the typed part is shown; the
              // curriculum prefix is hidden but still saved as "<prefix> <typed>".
              <div
                className='step3-sheet-class-name'
                style={{
                  ...inputStyle(tokens),
                  ...(showError(issues.includes('name')) ? {borderColor: tokens.danger} : null),
                }}
              >
                <input
                  id={nameInputId}
                  type='text'
                  value={draft.name}
                  placeholder={t('LEVEL_NAME_PLACEHOLDER')}
                  aria-label={t('LEVEL_NAME')}
                  onChange={(e) => setDraft((prev) => ({...prev, name: e.target.value}))}
                  style={{color: tokens.text, fontSize: '0.92rem'}}
                  className='step3-sheet-class-base'
                />
              </div>
            ) : (
              <input
                id={nameInputId}
                type='text'
                value={draft.name}
                placeholder={t('LEVEL_NAME_PLACEHOLDER')}
                onChange={(e) => setDraft((prev) => ({...prev, name: e.target.value}))}
                style={inputStyle(tokens)}
                className='step3-sheet-input'
              />
            )}
            {showError(duplicateLevelName) && errorText(t('LEVEL_NAME_DUPLICATE'))}
            {showError(issues.includes('name')) && errorText(t('NEED_LEVEL_NAME'))}
          </section>

          {/* 2. Classes & Sections */}
          <section>
            {sectionHeader(2, t('CLASSES_SECTIONS'), t('CLASSES_SECTIONS_HINT'))}
            <div className='step3-sheet-classes'>
              <div className='step3-sheet-class-row is-header' style={{color: tokens.muted}}>
                <span>{t('CLASS_NAME')}</span>
                <span>{t('SECTION_COUNT')}</span>
                <span>{t('SECTION_NAMES')}</span>
                <span />
              </div>
              {draft.classes.map((cls) => (
                <div key={cls.id} className='step3-sheet-class-row'>
                  {taggedClassIds.has(cls.id) ? (
                    // One field showing only the typed base name. The curriculum prefix is
                    // hidden from the UI but is still put back in front on save, so the stored
                    // name and every payload keep it ("FBISE Pre-primary 1").
                    <div
                      className='step3-sheet-class-name'
                      style={{
                        ...inputStyle(tokens),
                        ...(showError(isBlankClassName(cls) || classExistsInCurriculum(cls))
                          ? {borderColor: tokens.danger}
                          : null),
                      }}
                    >
                      <input
                        type='text'
                        value={cls.name}
                        placeholder={t('CLASS_NAME_PLACEHOLDER')}
                        aria-label={t('CLASS_NAME')}
                        onChange={(e) => setClass(cls.id, {name: e.target.value})}
                        aria-invalid={showError(isBlankClassName(cls)) || undefined}
                        style={{color: tokens.text, fontSize: '0.92rem'}}
                        className='step3-sheet-class-base'
                      />
                    </div>
                  ) : (
                    <input
                      type='text'
                      value={cls.name}
                      placeholder={t('CLASS_NAME_PLACEHOLDER')}
                      aria-label={t('CLASS_NAME')}
                      onChange={(e) => setClass(cls.id, {name: e.target.value})}
                      aria-invalid={showError(isBlankClassName(cls)) || undefined}
                      style={{
                        ...inputStyle(tokens),
                        ...(showError(isBlankClassName(cls) || classExistsInCurriculum(cls))
                          ? {borderColor: tokens.danger}
                          : null),
                      }}
                      className='step3-sheet-input'
                    />
                  )}
                  <input
                    type='number'
                    min={1}
                    max={MAX_SECTIONS}
                    value={cls.sections.length}
                    aria-label={t('SECTION_COUNT')}
                    onChange={(e) => handleCountChange(cls, e.target.value)}
                    style={inputStyle(tokens)}
                    className='step3-sheet-input'
                  />
                  <input
                    type='text'
                    value={sectionText[cls.id] ?? cls.sections.join(', ')}
                    placeholder={t('SECTION_NAMES_PLACEHOLDER')}
                    aria-label={t('SECTION_NAMES')}
                    onChange={(e) =>
                      setSectionText((prev) => ({...prev, [cls.id]: e.target.value}))
                    }
                    onBlur={() => commitSectionText(cls)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        commitSectionText(cls)
                      }
                    }}
                    style={inputStyle(tokens)}
                    className='step3-sheet-input'
                  />
                  <button
                    type='button'
                    className='step2-edit-sheet-icon-btn is-delete'
                    aria-label={intl.formatMessage({id: 'GENERAL.DELETE'})}
                    disabled={draft.classes.length <= 1}
                    onClick={() => setConfirm({removeClass: cls.id})}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
            {showError(blankClassName) && errorText(t('CLASS_NAME_REQUIRED'))}
            {showError(duplicateClassName) && errorText(t('CLASS_NAME_DUPLICATE'))}
            {showError(classNameInOtherLevel) && errorText(t('CLASS_NAME_EXISTS_IN_CURRICULUM'))}
            {showError(issues.includes('classes') && !duplicateClassName && !blankClassName) &&
              errorText(t('NEED_CLASS'))}
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3'>
              <button type='button' className='step3-sheet-ghost-btn' onClick={addClass}>
                <Plus size={14} strokeWidth={2.5} />
                {t('ADD_CLASS')}
              </button>
              {mode === 'edit' ? (
                <button
                  type='button'
                  className='step3-sheet-outline-btn'
                  disabled={templateSections.length === 0}
                  onClick={() => setConfirm('applyAll')}
                >
                  <Copy size={14} />
                  {t('APPLY_SECTIONS_ALL_LEVELS')}
                </button>
              ) : (
                <button
                  type='button'
                  className='step3-sheet-outline-btn'
                  disabled={templateSections.length === 0 || draft.classes.length < 2}
                  onClick={applySectionsToAllClasses}
                >
                  <Copy size={14} />
                  {t('APPLY_SECTIONS_ALL_CLASSES')}
                </button>
              )}
            </div>
          </section>

          {/* 3. Subjects */}
          <section>
            {sectionHeader(3, t('SUBJECTS'), t('SUBJECTS_HINT'))}
            <div className='d-flex flex-wrap gap-2'>
              {chipSubjects.map((name) => {
                const selected = isSelected(name)
                return (
                  <button
                    key={name}
                    type='button'
                    className={`step3-subject-chip${selected ? ' is-selected' : ''}`}
                    aria-pressed={selected}
                    onClick={() => toggleSubject(name)}
                  >
                    {selected && <Check size={13} strokeWidth={3} />}
                    {name}
                  </button>
                )
              })}
            </div>
            <input
              id={customSubjectId}
              type='text'
              value={customSubjectInput}
              placeholder={t('CUSTOM_SUBJECT_PLACEHOLDER')}
              onChange={(e) => {
                setCustomSubjectInput(e.target.value)
                setSubjectAlreadyAdded(false)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  addCustomSubject()
                }
              }}
              aria-invalid={subjectAlreadyAdded || undefined}
              style={{
                ...inputStyle(tokens),
                marginTop: '0.9rem',
                ...(subjectAlreadyAdded ? {borderColor: tokens.danger} : null),
              }}
              className='step3-sheet-input'
            />
            {subjectAlreadyAdded && errorText(t('SUBJECT_ALREADY_ADDED'))}
            {showError(issues.includes('subjects')) && errorText(t('NEED_SUBJECT'))}
          </section>
        </div>

        {/* Same footer as the Institution Setup editors (flows under the content, transparent —
            the buttons carry their own surfaces). Delete level sits on the left in edit mode. */}
        <div className='step2-edit-sheet-footer'>
          {mode === 'edit' && onDelete && (
            <button
              type='button'
              className='step3-sheet-danger-btn'
              onClick={() => setConfirm('delete')}
            >
              <Trash2 size={14} />
              {t('DELETE_LEVEL')}
            </button>
          )}
          <div className='d-flex gap-2 ms-auto'>
            <button type='button' className='step2-edit-sheet-cancel' onClick={requestClose}>
              {intl.formatMessage({id: 'GENERAL.CANCEL'})}
            </button>
            <button
              type='button'
              className='step2-edit-sheet-save'
              onClick={handleSave}
              // Editing an existing level: nothing to save until something changed.
              disabled={mode === 'edit' && !isDirty}
            >
              {mode === 'edit'
                ? intl.formatMessage({id: 'GENERAL.SAVE_CHANGES'})
                : t('ADD_LEVEL_BTN')}
            </button>
          </div>
        </div>
      </SheetModal>

      <WizardConfirmDialog
        open={confirm === 'discard'}
        tone='primary'
        isDark={isDark}
        title={t('DISCARD_TITLE')}
        body={t('DISCARD_BODY')}
        confirmLabel={intl.formatMessage({id: 'GENERAL.DISCARD'})}
        cancelLabel={t('KEEP_EDITING')}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          setConfirm(null)
          onClose()
        }}
      />
      <WizardConfirmDialog
        open={confirm === 'delete'}
        tone='danger'
        isDark={isDark}
        title={intl.formatMessage({id: 'MENU.ARE_YOU_SURE?'})}
        body={t('DELETE_LEVEL_CONFIRM')}
        confirmLabel={intl.formatMessage({id: 'GENERAL.DELETE'})}
        cancelLabel={intl.formatMessage({id: 'GENERAL.CANCEL'})}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          setConfirm(null)
          onDelete?.()
        }}
      >
        {levelStudentsWarning && <StudentsWarning text={levelStudentsWarning} />}
      </WizardConfirmDialog>
      <WizardConfirmDialog
        open={confirm === 'applyAll'}
        tone='primary'
        isDark={isDark}
        title={t('APPLY_SECTIONS_ALL_LEVELS')}
        body={t('APPLY_SECTIONS_CONFIRM', {
          count: templateSections.length,
          names: templateSections.join(', '),
        })}
        confirmLabel={t('APPLY')}
        cancelLabel={intl.formatMessage({id: 'GENERAL.CANCEL'})}
        onCancel={() => setConfirm(null)}
        onConfirm={confirmApplyToAllLevels}
      />
      <WizardConfirmDialog
        open={typeof confirm === 'object' && confirm !== null}
        tone='danger'
        isDark={isDark}
        title={intl.formatMessage({id: 'MENU.ARE_YOU_SURE?'})}
        body={t('DELETE_CLASS_CONFIRM')}
        confirmLabel={intl.formatMessage({id: 'GENERAL.DELETE'})}
        cancelLabel={intl.formatMessage({id: 'GENERAL.CANCEL'})}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          if (typeof confirm === 'object' && confirm !== null) removeClass(confirm.removeClass)
          setConfirm(null)
        }}
      >
        {classStudentsWarning && <StudentsWarning text={classStudentsWarning} />}
      </WizardConfirmDialog>
    </>
  )
}

export default Step3LevelDrawer
